/**
 * Broker-auth token provider — REAL login via the RCI-Auth broker.
 *
 * This is the PRODUCTION auth flow for the Olympic install. Unlike dev-auth
 * (which mints from an unguarded `_dev/mint` endpoint) or prod-auth (which
 * scripts a demo email+password login against the Oxbo IdP), this module runs a
 * standard OpenID Connect **authorization-code + PKCE** flow against the
 * RCI-Auth broker (`https://auth.roderickc.com`) as a **public client** — no
 * client secret, so the whole exchange happens safely in the browser.
 *
 * The broker's `.well-known/openid-configuration` advertises:
 *   response_types_supported          = ["code"]
 *   grant_types_supported             = ["authorization_code","refresh_token"]
 *   code_challenge_methods_supported  = ["S256"]            (PKCE)
 *   token_endpoint_auth_methods       = ["client_secret_post","none"]  ← "none" = public
 *
 * Flow:
 *   1. On start, if unauthenticated, `beginLogin()` generates a PKCE
 *      verifier/challenge + state, stashes them in sessionStorage, and redirects
 *      the browser to the broker `/authorize`.
 *   2. The broker authenticates the user (Entra SSO behind it) and redirects back
 *      to `${redirect_uri}` (`/auth/callback`) with `?code=…&state=…`.
 *   3. `completeLogin()` (called by the callback route) validates state, POSTs the
 *      code + PKCE verifier to `/token`, and stores the RS256 access token
 *      (+ refresh token) in memory.
 *   4. The access token is attached as `Authorization: Bearer <token>` to BOTH
 *      Oxbo-M and Oxbo-P calls; each verifies it against the broker JWKS.
 *   5. A margin before `exp`, the token is rotated via the refresh grant.
 *
 * Gated entirely behind `VITE_BROKER_AUTH=true`. When unset/false none of this
 * runs (dev-auth / prod-auth / MSW cover the other build modes).
 *
 * Config (build-time):
 *   VITE_BROKER_AUTH        "true" to enable this provider
 *   VITE_BROKER_URL         broker origin (default https://auth.roderickc.com)
 *   VITE_BROKER_CLIENT_ID   OIDC client_id registered on the broker (e.g. "olympic")
 *   VITE_BROKER_REDIRECT_URI absolute callback URL (default `${origin}/auth/callback`)
 *   VITE_BROKER_SCOPE       space-delimited scopes (default "openid profile email")
 */

/** Refresh this many ms before the token's expiry so a call never races exp. */
const REFRESH_LEAD_MS = 2 * 60 * 1000; // 2 min
/** Fallback access-token lifetime if the JWT carries no readable `exp`. */
const FALLBACK_LIFETIME_MS = 15 * 60 * 1000;
/** Assume at least this much runway so a bad clock can't schedule exp in the past. */
const MIN_LIFETIME_MS = 60 * 1000;
/** Back-off before retrying a failed refresh. */
const RETRY_DELAY_MS = 5 * 1000;

/** sessionStorage keys for the in-flight PKCE handshake. */
const SS_VERIFIER = "oxbo.broker.pkce_verifier";
const SS_STATE = "oxbo.broker.state";
/** sessionStorage key for the refresh token (survives a same-tab reload). */
const SS_REFRESH = "oxbo.broker.refresh_token";

/** Whether broker-auth is active for this build. */
export function isBrokerAuthEnabled(): boolean {
  return import.meta.env.VITE_BROKER_AUTH === "true";
}

function brokerBase(): string {
  return (import.meta.env.VITE_BROKER_URL ?? "https://auth.roderickc.com").replace(/\/$/, "");
}
/**
 * Base for the OIDC *token* endpoint. This is a browser fetch (XHR), so it must
 * be SAME-ORIGIN to avoid the broker's CORS allowlist: nginx proxies
 * `/api/auth/*` → the broker server-side, so the browser only ever talks to our
 * own origin. `VITE_BROKER_TOKEN_URL` overrides; default is same-origin
 * `/api/auth`. (The `/authorize` redirect is a top-level navigation straight to
 * the broker — no CORS — so it uses `brokerBase()`, not this.)
 */
function tokenBase(): string {
  return (import.meta.env.VITE_BROKER_TOKEN_URL ?? "/api/auth").replace(/\/$/, "");
}
function authorizeUrl(): string {
  return `${brokerBase()}/authorize`;
}
function tokenUrl(): string {
  return `${tokenBase()}/token`;
}
function clientId(): string {
  return import.meta.env.VITE_BROKER_CLIENT_ID ?? "olympic";
}
function redirectUri(): string {
  const configured = import.meta.env.VITE_BROKER_REDIRECT_URI;
  if (configured) return configured;
  if (typeof window !== "undefined") return `${window.location.origin}/auth/callback`;
  return "/auth/callback";
}
function scope(): string {
  return import.meta.env.VITE_BROKER_SCOPE ?? "openid profile email";
}
/**
 * RP-initiated logout endpoint. Like `/authorize`, this is a top-level browser
 * navigation (not XHR), so it hits the broker origin directly — no CORS. The
 * broker clears its SSO cookie and redirects back to `post_logout_redirect_uri`.
 * `VITE_BROKER_LOGOUT_URL` overrides (e.g. to route same-origin via nginx).
 */
function logoutUrl(): string {
  return import.meta.env.VITE_BROKER_LOGOUT_URL ?? `${brokerBase()}/auth/logout`;
}
/** Where the broker sends the browser after logout — back to the app root. */
function postLogoutRedirectUri(): string {
  const configured = import.meta.env.VITE_BROKER_POST_LOGOUT_URI;
  if (configured) return configured;
  if (typeof window !== "undefined") return `${window.location.origin}/`;
  return "/";
}

let accessToken: string | null = null;
let refreshToken: string | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let inFlight: Promise<string | null> | null = null;

/** The current broker access token, or null if not yet authenticated. */
export function getBrokerToken(): string | null {
  return accessToken;
}

/** True once a valid access token is held (drives the app-load auth gate). */
export function isBrokerAuthenticated(): boolean {
  return accessToken != null;
}

// ── PKCE helpers ─────────────────────────────────────────────────────────────

/** URL-safe base64 of raw bytes (RFC 7636 / RFC 4648 §5, no padding). */
function base64UrlEncode(bytes: Uint8Array): string {
  let str = "";
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** A cryptographically-random URL-safe string of `len` chars (PKCE verifier / state). */
function randomUrlSafe(len: number): string {
  const bytes = new Uint8Array(len);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes).slice(0, len);
}

/** S256 code challenge for a PKCE verifier. */
async function s256Challenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(digest));
}

// ── Claim decoding ───────────────────────────────────────────────────────────

/** Decode an arbitrary claim map from a JWT payload, or null if unreadable. */
function decodeClaims(token: string): Record<string, unknown> | null {
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Decode a JWT's `exp` (seconds) → absolute ms, or null if unreadable. */
function readExpMs(token: string): number | null {
  const claims = decodeClaims(token);
  const exp = claims?.exp;
  return typeof exp === "number" ? exp * 1000 : null;
}

/**
 * Role/entitlement claims off the current access token. The broker projects the
 * user's domain roles (mapped from Entra groups) onto the token; we read the
 * common shapes — `org_roles` / `roles` (array) or `role` (scalar) — and return
 * a de-duped string list, so the UI can gate super_admin vs user features.
 */
export function getBrokerRoles(): string[] {
  if (!accessToken) return [];
  const claims = decodeClaims(accessToken);
  if (!claims) return [];
  const out = new Set<string>();
  for (const key of ["org_roles", "roles"]) {
    const v = claims[key];
    if (Array.isArray(v)) v.forEach((r) => typeof r === "string" && out.add(r));
  }
  if (typeof claims.role === "string") out.add(claims.role);
  return [...out];
}

/**
 * True when the token carries a GLOBAL system-admin role (`user_admin` /
 * `super_admin` in the `system_roles` claim). Drives the `X-OXBO-Admin-Mode`
 * header so RCI staff get cross-tenant (admin-mode) reads. Read from
 * `system_roles` specifically — NOT the per-tenant product `roles` — so a mere
 * tenant admin never triggers cross-org access.
 */
export function isGlobalAdmin(): boolean {
  if (!accessToken) return false;
  const claims = decodeClaims(accessToken);
  const sr = claims?.system_roles;
  if (!Array.isArray(sr)) return false;
  return sr.some((r) => r === "super_admin" || r === "user_admin");
}

/** The user's org/tenant claim off the token (`org` / `org_id` / `tid`), or null. */
export function getBrokerOrg(): string | null {
  if (!accessToken) return null;
  const claims = decodeClaims(accessToken);
  if (!claims) return null;
  for (const key of ["org", "org_id", "tid"]) {
    const v = claims[key];
    if (typeof v === "string" && v) return v;
  }
  return null;
}

// ── Logout ───────────────────────────────────────────────────────────────────

/** Forget all in-memory + persisted session state and cancel the refresh loop. */
function clearSession(): void {
  accessToken = null;
  refreshToken = null;
  inFlight = null;
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  try {
    sessionStorage.removeItem(SS_VERIFIER);
    sessionStorage.removeItem(SS_STATE);
    sessionStorage.removeItem(SS_REFRESH);
  } catch {
    /* sessionStorage may be unavailable */
  }
}

/**
 * Log the user out: (a) forget the SPA's stored broker tokens/session so the app
 * no longer holds the current identity, then (b) navigate to the broker
 * RP-initiated logout so the broker clears its SSO cookie and redirects back to
 * the app root — where, unauthenticated, the app kicks off broker login again and
 * the user can pick a different account. The logout nav is top-level (no CORS).
 */
export function logout(): void {
  clearSession();
  if (typeof window === "undefined") return;
  const params = new URLSearchParams({
    post_logout_redirect_uri: postLogoutRedirectUri(),
  });
  window.location.assign(`${logoutUrl()}?${params.toString()}`);
}

// ── Authorization-code + PKCE handshake ──────────────────────────────────────

/**
 * Kick off login: generate PKCE + state, stash them, and redirect the browser to
 * the broker `/authorize`. Never returns (navigates away) unless `crypto.subtle`
 * is unavailable, in which case it throws.
 */
export async function beginLogin(): Promise<void> {
  const verifier = randomUrlSafe(64);
  const state = randomUrlSafe(32);
  const challenge = await s256Challenge(verifier);
  sessionStorage.setItem(SS_VERIFIER, verifier);
  sessionStorage.setItem(SS_STATE, state);

  const params = new URLSearchParams({
    client_id: clientId(),
    response_type: "code",
    redirect_uri: redirectUri(),
    scope: scope(),
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });
  window.location.assign(`${authorizeUrl()}?${params.toString()}`);
}

interface TokenResponse {
  access_token?: string;
  refresh_token?: string;
  token_type?: string;
  expires_in?: number;
}

/** Exchange an authorization code (+ PKCE verifier) for tokens. Throws on non-ok. */
async function exchangeCode(code: string, verifier: string): Promise<TokenResponse> {
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    redirect_uri: redirectUri(),
    client_id: clientId(),
    code_verifier: verifier,
  });
  const res = await fetch(tokenUrl(), {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });
  if (!res.ok) {
    throw new Error(`broker token exchange failed: ${res.status} ${res.statusText}`);
  }
  const json = (await res.json()) as TokenResponse;
  if (!json.access_token) throw new Error("broker token exchange returned no access_token");
  return json;
}

/**
 * Complete login at the `/auth/callback` route: validate `state` against the
 * stashed value, exchange the `code`, store the tokens, and arm the refresh
 * loop. Returns true on success. Clears the one-shot PKCE/state stash either way.
 * Throws with a descriptive message on any validation / exchange failure so the
 * callback screen can surface it.
 */
export async function completeLogin(search: string): Promise<boolean> {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const err = params.get("error");
  if (err) {
    clearHandshake();
    throw new Error(`broker returned error: ${err} ${params.get("error_description") ?? ""}`.trim());
  }
  const code = params.get("code");
  const returnedState = params.get("state");
  const expectedState = sessionStorage.getItem(SS_STATE);
  const verifier = sessionStorage.getItem(SS_VERIFIER);
  clearHandshake();

  if (!code) throw new Error("broker callback missing authorization code");
  if (!verifier) throw new Error("broker callback missing PKCE verifier (session lost)");
  if (!returnedState || returnedState !== expectedState) {
    throw new Error("broker callback state mismatch (possible CSRF)");
  }

  const tokens = await exchangeCode(code, verifier);
  storeTokens(tokens);
  return true;
}

/** Drop the one-shot PKCE verifier + state from sessionStorage. */
function clearHandshake(): void {
  sessionStorage.removeItem(SS_VERIFIER);
  sessionStorage.removeItem(SS_STATE);
}

function storeTokens(tokens: TokenResponse): string {
  accessToken = tokens.access_token ?? null;
  if (tokens.refresh_token) {
    refreshToken = tokens.refresh_token;
    try {
      sessionStorage.setItem(SS_REFRESH, tokens.refresh_token);
    } catch {
      /* sessionStorage may be unavailable; in-memory refresh still works this tab */
    }
  }
  if (accessToken) scheduleRefresh(accessToken);
  return accessToken ?? "";
}

/** Schedule the next refresh a safe margin before the access token expires. */
function scheduleRefresh(token: string): void {
  if (refreshTimer) clearTimeout(refreshTimer);
  const expMs = readExpMs(token);
  const lifetime = expMs ? expMs - Date.now() : FALLBACK_LIFETIME_MS;
  const delay = Math.max(MIN_LIFETIME_MS, lifetime - REFRESH_LEAD_MS);
  refreshTimer = setTimeout(() => {
    void renewBrokerToken();
  }, delay);
}

/** Rotate the refresh token → new token pair. Throws if none / non-ok. */
async function refreshOnce(): Promise<TokenResponse> {
  if (!refreshToken) throw new Error("broker-auth: no refresh token to rotate");
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: refreshToken,
    client_id: clientId(),
  });
  const res = await fetch(tokenUrl(), {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });
  if (!res.ok) {
    throw new Error(`broker refresh failed: ${res.status} ${res.statusText}`);
  }
  const json = (await res.json()) as TokenResponse;
  if (!json.access_token) throw new Error("broker refresh returned no access_token");
  return json;
}

/**
 * Renew the access token via the refresh grant. On failure, retries with a short
 * back-off (keeping any still-valid token) so a transient broker blip never
 * leaves the app permanently token-less. If there is no refresh token at all the
 * only recovery is a fresh interactive login, which the app-load gate triggers.
 * Concurrent callers share one in-flight renew.
 */
export function renewBrokerToken(): Promise<string | null> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const tokens = await refreshOnce();
      return storeTokens(tokens);
    } catch (err) {
      if (typeof console !== "undefined") {
        console.warn("[brokerAuth] renew failed, retrying", err);
      }
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        void renewBrokerToken();
      }, RETRY_DELAY_MS);
      return accessToken;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/**
 * Initialize broker auth at app start. No-op unless enabled. Behavior:
 *   • On the `/auth/callback` route → do nothing here; the callback screen calls
 *     `completeLogin()` itself (we must not consume the code twice).
 *   • Else if a refresh token survives from an earlier same-tab login → try a
 *     silent refresh to restore the session without a round-trip.
 *   • Else if still unauthenticated → begin the interactive login (redirect).
 * Never throws — a failed silent refresh falls through to interactive login.
 */
export async function initBrokerAuth(): Promise<void> {
  if (!isBrokerAuthEnabled()) return;
  if (typeof window === "undefined") return;

  // The callback route handles its own exchange; don't interfere.
  if (window.location.pathname === "/auth/callback") return;

  // Try to restore a prior session silently via a surviving refresh token.
  const savedRefresh = sessionStorage.getItem(SS_REFRESH);
  if (savedRefresh && !accessToken) {
    refreshToken = savedRefresh;
    const token = await renewBrokerToken();
    if (token) return;
  }

  // Still unauthenticated → start the interactive login.
  if (!accessToken) {
    await beginLogin();
  }
}

/** Test-only: clear all in-memory + session state and timers. */
export function __resetBrokerAuthForTests(): void {
  clearSession();
}
